// Logic workspace: everything that RUNS, in the order a user meets it —
// opening a row, changing a field, finishing a row, clicking a button,
// saving, approval, after the save. One timeline instead of nine tree nodes;
// each item edits in the inspector on the right with the same editor the
// Design workspace opens.

import { useMemo, useState } from 'react'
import type { TransactionModel, TreeNode } from './types'
import { logicStages, formTitle, type LogicKind, type LogicStage, type SetupItem } from './setupCatalog'
import { allSqlModels } from '@twasta/page-runtime/txn/utils'

interface Props {
  model: TransactionModel
  selectedNode: TreeNode | null
  onSelect: (node: TreeNode) => void
  onOpenSettings: (node?: TreeNode) => void
  /** The inspector (docked / floating), rendered by the shell. */
  inspector: React.ReactNode
  compact?: boolean
}

const KIND_STYLE: Record<LogicKind, React.CSSProperties> = {
  Rule: { color: 'var(--color-success, #0e6027)', background: 'var(--color-success-weak, rgba(36,161,72,.14))' },
  Code: { color: '#6929c4', background: 'rgba(105,41,196,.10)' },
  API: { color: 'var(--color-warning, #8e4b00)', background: 'var(--color-warning-weak, rgba(210,161,6,.16))' },
  Button: { color: 'var(--color-accent)', background: 'var(--color-accent-weak)' },
  Gate: { color: 'var(--color-bg-secondary)', background: 'var(--color-text)' },
}
// Card tags use the filter's words, so 'No-code rules' in the panel matches 'No code' on a card.
const KIND_LABEL: Record<LogicKind, string> = { Rule: 'No code', Code: 'Code', API: 'API call', Button: 'Button', Gate: 'Approval' }

const sameNode = (a: TreeNode | null, b: TreeNode) =>
  !!a && a.type === b.type && a.formIndex === b.formIndex && a.columnIndex === b.columnIndex && a.actionIndex === b.actionIndex

export default function LogicWorkspace({ model, selectedNode, onSelect, onOpenSettings, inspector, compact }: Props) {
  const [scope, setScope] = useState<string>('all')
  const [written, setWritten] = useState<Record<'Rule' | 'Code' | 'API', boolean>>({ Rule: true, Code: true, API: true })

  const stages = useMemo(() => logicStages(model), [model])
  const all = stages.flatMap(s => s.items)
  const scopes = useMemo(() => {
    const out: Array<{ key: string; label: string; n: number }> = [
      { key: 'all', label: 'All forms', n: all.length },
      { key: 'record', label: 'Whole record', n: all.filter(i => i.scopeKey === 'record').length },
    ]
    allSqlModels(model).forEach((e, fi) => out.push({
      key: `form:${fi}`, label: formTitle(e.sm, fi), n: all.filter(i => i.scopeKey === `form:${fi}`).length,
    }))
    return out
  }, [model, all])

  const keep = (it: SetupItem) => {
    if (scope !== 'all' && it.scopeKey !== scope) return false
    if (it.kind === 'Rule' || it.kind === 'Code' || it.kind === 'API') return written[it.kind]
    return true
  }
  // Every stage always shows; an empty one is a single line with its + Add.
  const visible: Array<LogicStage & { hidden: number }> = stages
    .map(s => { const items = s.items.filter(keep); return { ...s, items, hidden: s.items.length - items.length } })
  // How many of each kind the picked form has (counts ignore the kind
  // filter itself, so unticking a box doesn't zero its own number).
  const kindCount = (k: LogicKind) => all.filter(i => i.kind === k && (scope === 'all' || i.scopeKey === scope)).length
  const filtered = scope !== 'all' || !written.Rule || !written.Code || !written.API
  const clearFilters = () => { setScope('all'); setWritten({ Rule: true, Code: true, API: true }) }

  // "+ Add" per stage: the list in the inspector where that kind of thing is added.
  const formFi = scope.startsWith('form:') ? Number(scope.slice(5)) : 0
  const addFor = (s: LogicStage): { label: string; act: () => void } => {
    switch (s.key) {
      case 'opened':
      case 'finished':
        return { label: '+ Add', act: () => onSelect({ type: 'form-row-events', formIndex: formFi, label: 'Row events' }) }
      case 'field':
        return { label: '+ Add', act: () => onSelect({ type: 'form-field-events', formIndex: formFi, label: 'Field events' }) }
      case 'button':
        return scope === 'record'
          ? { label: '+ Add button', act: () => onSelect({ type: 'object-actions', label: 'Record buttons' }) }
          : { label: '+ Add button', act: () => onSelect({ type: 'form-actions', formIndex: formFi, label: 'Form buttons' }) }
      case 'before':
        return scope === 'record'
          ? { label: '+ Add', act: () => onSelect({ type: 'record-saving', label: 'Before / after saving' }) }
          : { label: '+ Add', act: () => onSelect({ type: 'form-validations', formIndex: formFi, label: 'Validations' }) }
      case 'approval':
        return { label: 'Open in Settings', act: () => onOpenSettings({ type: 'approvals', label: 'Approvals' }) }
      case 'after':
        return { label: '+ Add', act: () => onSelect({ type: 'followup-actions', label: 'Follow-ups' }) }
    }
  }

  const scopeRow = (on: boolean): React.CSSProperties => ({
    font: 'inherit', fontSize: '0.9375rem', display: 'flex', gap: 8, alignItems: 'center', border: 'none',
    borderRadius: 6, padding: compact ? '10px' : '7px 10px', cursor: 'pointer', textAlign: 'left', width: '100%',
    background: on ? 'var(--color-accent-weak)' : 'transparent', fontWeight: on ? 600 : 400, color: 'var(--color-text)',
  })

  return (
    <div style={{ display: 'flex', flexWrap: compact ? 'wrap' : 'nowrap', flex: 1, minHeight: 0, minWidth: 0, alignItems: 'stretch', position: 'relative', overflow: compact ? 'auto' : 'hidden' }}>
      <aside aria-label="Filter logic" style={{
        flex: compact ? '1 1 100%' : '0 0 220px', background: 'var(--color-bg-secondary)',
        borderRight: compact ? 'none' : '1px solid var(--color-border)', borderBottom: compact ? '1px solid var(--color-border)' : 'none',
        padding: 16, display: 'flex', flexDirection: compact ? 'row' : 'column', flexWrap: compact ? 'wrap' : 'nowrap', gap: 18, overflow: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flex: 'none', width: '100%', flexBasis: compact ? '100%' : 'auto', borderBottom: '1px solid var(--color-border)', paddingBottom: 10 }}>
          <div style={{ fontSize: '0.8125rem', fontWeight: 600, letterSpacing: '0.06em', color: 'var(--color-text-secondary)' }}>FILTER</div>
          <button type="button" onClick={clearFilters} disabled={!filtered}
            title={filtered ? 'Show everything again' : 'Nothing is filtered'}
            style={{
              font: 'inherit', fontSize: '0.875rem', border: 'none', background: 'none', padding: 0,
              color: filtered ? 'var(--color-accent)' : 'var(--color-text-faint)', cursor: filtered ? 'pointer' : 'default',
            }}>Clear</button>
        </div>
        <div style={{ minWidth: compact ? 200 : 0 }}>
          <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text)', marginBottom: 8 }}>Show logic for</div>
          {compact ? (
            <select value={scope} onChange={e => setScope(e.target.value)} style={{ width: '100%', padding: 10 }}>
              {scopes.map(s => <option key={s.key} value={s.key}>{s.label} ({s.n})</option>)}
            </select>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {scopes.map(s => (
                <button key={s.key} type="button" aria-pressed={scope === s.key} style={scopeRow(scope === s.key)} onClick={() => setScope(s.key)}>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
                  <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', fontWeight: 400 }}>{s.n}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text)', marginBottom: 8 }}>Show only</div>
          <div style={{ display: 'flex', flexDirection: compact ? 'row' : 'column', gap: compact ? 14 : 6, fontSize: '0.9375rem', flexWrap: 'wrap' }}>
            {([['Rule', 'No-code rules'], ['Code', 'Code functions'], ['API', 'API calls']] as const).map(([k, lbl]) => (
              <label key={k} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: compact ? 0 : '0 10px 0 0' }}>
                <input type="checkbox" checked={written[k]} onChange={e => setWritten(w => ({ ...w, [k]: e.target.checked }))} />
                <span style={{ flex: 1 }}>{lbl}</span>
                <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>{kindCount(k)}</span>
              </label>
            ))}
          </div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 6, lineHeight: 1.4 }}>
            Buttons and approval always show.
          </div>
        </div>
      </aside>

      <main style={{ flex: '1 1 0', minWidth: compact ? 0 : 340, padding: compact ? '16px 12px' : '20px 24px', overflow: 'auto', background: 'var(--color-bg)' }}>
        <h1 style={{ margin: '0 0 4px', fontSize: '1.1875rem', fontWeight: 600 }}>What runs, and when</h1>
        <div style={{ fontSize: '0.9375rem', color: 'var(--color-text-secondary)', marginBottom: 18 }}>
          In the order a user meets it — opening a row, changing a field, clicking a button, saving.
        </div>
        <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {visible.map((st, si) => {
            const add = addFor(st)
            return (
              <li key={st.key} style={{ display: 'flex', gap: 14 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 'none' }}>
                  <span style={{
                    width: 26, height: 26, borderRadius: '50%', background: 'var(--color-text)', color: 'var(--color-bg-secondary)',
                    fontSize: '0.875rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{st.n}</span>
                  {si < visible.length - 1 && <span style={{ width: 2, flex: 1, background: 'var(--color-border)', minHeight: 12 }} />}
                </div>
                <div style={{ flex: 1, minWidth: 0, paddingBottom: 18 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 10px', marginTop: 3 }}>
                    <h2 style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 600 }}>{st.title}</h2>
                    <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>{st.sub}</span>
                    <button type="button" onClick={add.act} style={{
                      marginLeft: 'auto', font: 'inherit', fontSize: '0.9375rem', border: 'none', background: 'none',
                      color: 'var(--color-accent)', cursor: 'pointer',
                    }}>{add.label}</button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                    {st.items.length === 0 && (
                      <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                        {st.hidden > 0
                          ? `${st.hidden} hidden by the filter`
                          : st.key === 'approval' ? 'No approval — the record saves directly.' : 'Nothing set up'}
                      </div>
                    )}
                    {st.items.map(it => {
                      const sel = sameNode(selectedNode, it.node)
                      return (
                        <button key={it.id} type="button" onClick={() => (it.node.type === 'approvals' ? onOpenSettings(it.node) : onSelect(it.node))}
                          style={{
                            font: 'inherit', display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                            background: 'var(--color-bg-secondary)', borderRadius: 8, padding: sel ? '8px 11px' : '9px 12px',
                            cursor: 'pointer', color: 'var(--color-text)',
                            border: sel ? '2px solid var(--color-accent)' : '1px solid var(--color-border)',
                          }}>
                          <span style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: '0.9375rem', fontWeight: 500, fontFamily: it.mono ? 'var(--font-mono)' : 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name}</span>
                            <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.detail}</span>
                          </span>
                          {!compact && (
                            <span style={{ fontSize: '0.875rem', background: 'var(--color-surface-2)', borderRadius: 4, padding: '2px 8px', whiteSpace: 'nowrap' }}>{it.scopeLabel}</span>
                          )}
                          <span style={{ fontSize: '0.8125rem', fontWeight: 600, borderRadius: 4, padding: '2px 8px', whiteSpace: 'nowrap', ...KIND_STYLE[it.kind] }}>{KIND_LABEL[it.kind]}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              </li>
            )
          })}
        </ol>
      </main>
      {inspector}
    </div>
  )
}
